import isEqual from "lodash/isEqual";
import { createSelector } from "reselect";

import { deviceStatusesSelector } from "cortex/ui/src/redux-store/device-statuses";
import { devicesSelector } from "cortex/ui/src/redux-store/devices/selectors";
import { fullDevicesSelector } from "cortex/ui/src/redux-store/ui-state/full-devices/selectors";

interface DeviceWithStatus {
  id: string | number;
  status?: unknown;
  [key: string]: unknown;
}

type DeviceById = Record<string, DeviceWithStatus>;
type StatusById = Record<string, unknown>;

function makeCachedGetDeviceWithStatusById() {
  let state: DeviceById = {};
  function _getDeviceWithStatusById(devices: DeviceById, statuses: StatusById) {
    const newState = Object.values(devices).reduce<DeviceById>((acc, d) => {
      const status = statuses[d.id];

      acc[d.id] = {
        ...d,
        status: status ? status : d.status,
      };

      return acc;
    }, {});

    if (!isEqual(state, newState)) {
      state = newState;
    }

    return state;
  }

  return _getDeviceWithStatusById;
}

function makeCachedGetDeviceWithStatusArray() {
  let state: DeviceWithStatus[] = [];
  function _getDeviceWithStatusArray(
    devices: DeviceWithStatus[],
    statuses: StatusById,
  ) {
    const newState = devices.map((d) => {
      const status = statuses[d.id];

      return {
        ...d,
        status: status ? status : d.status,
      };
    });

    if (!isEqual(state, newState)) {
      state = newState;
    }

    return state;
  }

  return _getDeviceWithStatusArray;
}

/*
 * Devices and device statuses are globally shared across the application.
 * Shared caching should not break anything.
 * This takes and returns a byId object of devices.
 */
const deviceWithStatusSelector = createSelector(
  devicesSelector,
  deviceStatusesSelector,
  makeCachedGetDeviceWithStatusById(),
);

/*
 * Full devices are a device subset, so global caching might break views.
 * This takes and returns an array of devices.
 */
function makeFullDeviceWithStatusSelector() {
  return createSelector(
    fullDevicesSelector,
    deviceStatusesSelector,
    makeCachedGetDeviceWithStatusArray(),
  );
}

export { deviceWithStatusSelector, makeFullDeviceWithStatusSelector };
